import { createBrowserHistory, History } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import { Hub, Amplify } from '@aws-amplify/core';
import { CognitoConfiguration, getConfiguration } from './GlobalFunctions';
import {
  dataResidencyManager,
  SupportedDataResidency,
} from './DataResidencyManager';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';
import user from './UserManagement';
import game from './GameState';
import entry from './EntryState';
import signup from './SignUpState';
import events, { push, AuthEvent } from './AuthenticationEvents';

const store = configureStore({
  reducer: {
    user,
    game,
    entry,
    signup,
    events,
  },
  // CAUTION: we are explicitly turning off the warnings about putting in
  // non-serizliable objects into the store. This is because we are using
  // the CognitoUserProfileType which is an object with functions that we want
  // access to such as getSignInUserSession.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const myBrowserHistory: History = createBrowserHistory();

// Deprecated: Use getCognitoConfiguration() instead for data residency support
export const cognitoConfiguration = getConfiguration(
  'cognito',
) as CognitoConfiguration;

// Get data residency-aware Cognito configuration
export async function getCognitoConfiguration(): Promise<CognitoConfiguration> {
  const dataResidency = dataResidencyManager.getCurrentDataResidency();
  const configModule =
    await dataResidencyManager.getCognitoConfiguration(dataResidency);
  const config = configModule.default || configModule;

  // Map to expected CognitoConfiguration interface
  return {
    userPool: config.userPool || config.userPoolId,
    identityPool: config.identityPool,
    clientId: config.clientId,
    region: config.region,
    hostedUICognitoDomain:
      config.hostedUICognitoDomain ||
      getConfiguration('cognito').hostedUICognitoDomain,
    hostedUISignInCallbackPath:
      config.hostedUISignInCallbackPath ||
      getConfiguration('cognito').hostedUISignInCallbackPath ||
      '/oauth/login',
    hostedUISignOutCallbackPath:
      config.hostedUISignOutCallbackPath ||
      getConfiguration('cognito').hostedUISignOutCallbackPath ||
      '/',
  };
}

export async function initializeApplicationState() {
  // console.debug(`Entered initializeApplicationState()`);

  try {
    // Initialize data residency first
    await dataResidencyManager.initializeDataResidency();

    // Use data residency-aware configuration
    await configureAmplifyWithDataResidency();
  } catch (error) {
    console.error('Failed to initialize application state:', error);
    // Fallback to US configuration if initialization fails
    console.log('Falling back to US configuration');
    await dataResidencyManager.setDataResidency(SupportedDataResidency.US);
    await configureAmplifyWithDataResidency();
  }

  // console.debug(`Exiting initializeApplicationState()`);
}

// Legacy function for backward compatibility
function configureAmplify(newSignInCallbackPath?: string) {
  const callbackUrlRoot = `${document.location.protocol}//${document.location.host}`;
  const signInCallback = `${callbackUrlRoot}${
    newSignInCallbackPath || cognitoConfiguration.hostedUISignInCallbackPath
  }`;
  const signOutCallback = `${callbackUrlRoot}${cognitoConfiguration.hostedUISignOutCallbackPath}`;
  console.info(
    `%c Configuring amplify (legacy)`,
    'background: #222; color: #bada55',
  );

  Amplify.configure({
    Analytics: {
      disabled: true,
    },
    Auth: {
      userPoolId: cognitoConfiguration.userPool,
      identityPoolId: cognitoConfiguration.identityPool,
      userPoolWebClientId: cognitoConfiguration.clientId,
    },
    oauth: {
      domain: cognitoConfiguration.hostedUICognitoDomain,
      scope: [
        'phone',
        'email',
        'profile',
        'openid',
        'aws.cognito.signin.user.admin',
      ],
      redirectSignIn: signInCallback,
      redirectSignOut: signOutCallback,
      responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
    },
  });

  Hub.listen('auth', pushEventIntoQueue);
}

// New data residency-aware configuration
async function configureAmplifyWithDataResidency(
  newSignInCallbackPath?: string,
) {
  try {
    const dataResidency = dataResidencyManager.getCurrentDataResidency();
    console.info(
      `%c Configuring amplify for ${dataResidency} data residency`,
      'background: #222; color: #bada55',
    );

    // Use the data residency-aware helper to configure Amplify
    await initializeAmplifyForDataResidency(dataResidency);

    // Note: Hub listener is already set up in initializeAmplifyForDataResidency
  } catch (error) {
    console.error(
      'Failed to configure Amplify with data residency, falling back to legacy config:',
      error,
    );
    // Fallback to legacy configuration
    configureAmplify(newSignInCallbackPath);
  }
}

function pushEventIntoQueue({ payload }: { payload: AuthEvent }) {
  console.info(
    `%c OauthLogin pushing event `,
    'background: #222; color: #bada55',
    payload,
  );

  getStore().dispatch(push(payload));
}

export function getStore() {
  return store;
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
